import { toPositiveInt } from '@/utils/format'
import type { PaginationResponse } from './pagination.type'
import type { SalesRep } from './customer.type'
import type { Branch } from './order.type'

// User entity の列だけ（relation を含まない）
export type User = {
  id: number
  name: string
  email: string
  canAccessAllBranches: boolean // true は全社の顧客、false は所属支店の顧客だけが見える
  isManager: boolean
  canApproveDealAndBackupSales: boolean
  isSystemAdmin: boolean // true なら canAccessAllBranches も必ず true（DB 制約）
  branchOrgId: number | null // null は所属支店なし（全支店を参照できる人だけがなれる）
  salesrepNumber: number | null
  preferences: UserPreferences
  shouldForceLogout: boolean
  deactivatedAt: Date | null
  createdAt: Date
  updatedAt: Date
}

type LocalCredential = {
  userId: number
  createdAt: Date
  updatedAt: Date
}

type SalesApiRole = 'sales' | 'sales_atm' | 'office' | 'hq' | 'admin'

// WeConnect アカウントの紐付け。紐付けがある User の名前・System admin 以外の権限・支店・担当コードは、ログインのたびに API がここから計算して上書きする
type UserSalesApiIdentity = {
  salesApiAccountId: string
  email: string
  role: SalesApiRole
  org: string | null
  salesrepNumber: number | null
  createdAt: Date
  updatedAt: Date
}

type UserWithAffiliation = User & {
  branch: Branch | null
  salesrep: SalesRep | null
}

export type UserWithLocalCredential = UserWithAffiliation & {
  localCredential: LocalCredential | null
}

export type UserWithLoginMethods = UserWithLocalCredential & {
  salesApiIdentities: UserSalesApiIdentity[]
}

export type UserListItem = UserWithAffiliation & {
  hasLocalCredential: boolean
  isWeConnectLinked: boolean
}

export type BaseFontSize = 'medium' | 'large' | 'super-large'
export type DictationLang = 'en' | 'ja' | 'ko' | 'zh'
export const dictationLangOptions: { value: DictationLang; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'zh', label: 'Chinese' },
]
export type UserPreferences = {
  baseFontSize?: BaseFontSize
  dictationLang?: DictationLang
  enabledCustomerSchedule?: boolean
}

export type CustomerNote = {
  id: number
  userId: number
  account_number: number
  account_name: string
  note: string
  createdAt: Date
  updatedAt: Date
}

export type UserResponseDto = PaginationResponse<UserListItem>

export const UserStatusFilter = {
  all: 'all',
  active: 'active',
  deactivated: 'deactivated',
} as const
export type UserStatusFilter = (typeof UserStatusFilter)[keyof typeof UserStatusFilter]

export const UserLoginMethodFilter = {
  localOnly: 'localOnly',
  weConnectOnly: 'weConnectOnly',
  both: 'both',
} as const
export type UserLoginMethodFilter =
  (typeof UserLoginMethodFilter)[keyof typeof UserLoginMethodFilter]

export const UserRoleFilter = {
  systemAdmin: 'systemAdmin',
  manager: 'manager',
  allBranches: 'allBranches',
  dealApprover: 'dealApprover',
} as const
export type UserRoleFilter = (typeof UserRoleFilter)[keyof typeof UserRoleFilter]

export type GetUsersQuery = {
  page: number
  limit: number
  keyword?: string
  branchOrgId?: number
  status?: UserStatusFilter // 省略時は all（停止中も含む）
  loginMethod?: UserLoginMethodFilter
  roles?: UserRoleFilter[] // 複数指定はすべて持つ人に絞る（AND）
}

function firstString(value: unknown): string | undefined {
  const first = Array.isArray(value) ? value[0] : value
  return typeof first === 'string' ? first : undefined
}

export const parseUsersQuery = (params: Record<string, unknown>): Omit<GetUsersQuery, 'limit'> => {
  const status = firstString(params.status)
  const loginMethod = firstString(params.loginMethod)
  const roles = [params.roles].flat()
  const validRoles = Object.values(UserRoleFilter).filter((role) => roles.includes(role))
  return {
    page: toPositiveInt(firstString(params.page)) ?? 1,
    keyword: firstString(params.keyword)?.trim() || undefined,
    branchOrgId: toPositiveInt(firstString(params.branchOrgId)),
    status: Object.values(UserStatusFilter).find((value) => value === status) ?? 'active',
    loginMethod: Object.values(UserLoginMethodFilter).find((value) => value === loginMethod),
    roles: validRoles.length > 0 ? validRoles : undefined,
  }
}

export const buildUsersQuery = (filters: Omit<GetUsersQuery, 'limit'>) => ({
  page: filters.page > 1 ? String(filters.page) : undefined,
  keyword: filters.keyword?.trim() || undefined,
  branchOrgId: filters.branchOrgId?.toString(),
  status: filters.status === 'active' ? undefined : filters.status,
  loginMethod: filters.loginMethod,
  roles: filters.roles?.length ? filters.roles : undefined,
})

export type CreateUserPayload = {
  name: string
  email: string
  password: string
  branchOrgId: number | null
  salesrepNumber: number | null
  canAccessAllBranches: boolean
  isManager: boolean
  canApproveDealAndBackupSales: boolean
}

export type UpdateUserPayload = {
  name: string
  email: string
}

export type UpdateUserAccessPayload = {
  canAccessAllBranches: boolean
  isManager: boolean
  canApproveDealAndBackupSales: boolean
  isSystemAdmin: boolean
  branchOrgId: number | null
  salesrepNumber: number | null
}

export type CreateCustomerNotePayload = {
  account_number: number
  account_name: string
  note: string
}

export type UpdateCustomerNotePayload = {
  note: string
}
